/**
 * AccountManagementPage — GDPR self-service (#531, #532).
 * Feature doc: docs/canonical/features/future/v1.6-gdpr-self-service.md
 *
 * Two surfaces:
 *   1. Export my data — triggers JSON download
 *   2. Delete account — opens confirm modal → requests a confirmation
 *
 * The confirmation channel depends on the account (#2935): a real-email account
 * is emailed a magic link and confirms on /settings/delete-account/confirm; a
 * phone-only account (synthetic {phone}@sms.oro.fit email, no mailbox) is texted
 * a 6-digit code and enters it right here in the modal. Phone-only accounts CAN
 * sign into the web app (LoginModal auto-detects a phone number), so the web
 * flow must handle the code channel too — otherwise a phone-only user who
 * deletes on web is texted a code with nowhere to enter it (#2980).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { TopBar, Button, Card } from '../../components';
import { Download, Trash2, AlertTriangle, ExternalLink } from 'lucide-react';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import {
  useExportMyData,
  useRequestAccountDeletion,
  useConfirmAccountDeletionByCode,
} from '../../hooks/useAccountManagement';
import { ApiError } from '../../lib/api';
import type { BlockingClub } from '../../lib/api/account';

export default function AccountManagementPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useDocumentTitle('Account & Privacy');

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [blockingClubs, setBlockingClubs] = useState<BlockingClub[] | null>(null);
  const [requestSent, setRequestSent] = useState(false);
  // Phone-only accounts (#2935/#2980): a code was texted; collect it here.
  const [codeStep, setCodeStep] = useState(false);
  const [code, setCode] = useState('');

  const exportMutation = useExportMyData();
  const requestDeletionMutation = useRequestAccountDeletion();
  const confirmByCodeMutation = useConfirmAccountDeletionByCode();

  const handleExport = (): void => {
    exportMutation.mutate();
  };

  const handleRequestDelete = async (): Promise<void> => {
    setBlockingClubs(null);
    try {
      const data = await requestDeletionMutation.mutateAsync();
      if (data.channel === 'sms') {
        // Phone-only: a code was texted. Collect it in the modal.
        setCode('');
        setCodeStep(true);
      } else {
        // Real email: confirm via the emailed magic link.
        setRequestSent(true);
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'BLOCKED_SOLE_CLUB_OWNER') {
        const clubs = (err.details?.blocking_clubs as BlockingClub[] | undefined) ?? [];
        setBlockingClubs(clubs);
      }
      // Other errors surface via useOroMutation's toast.
    }
  };

  const handleSubmitCode = async (): Promise<void> => {
    if (code.length !== 6) return;
    try {
      await confirmByCodeMutation.mutateAsync(code);
      // Backend already invalidated every session + cleared the cookie. Wipe
      // client cache so no stale `useMe` repaints, then return home.
      queryClient.clear();
      navigate('/');
    } catch {
      // A wrong/expired code surfaces via useOroMutation's toast; keep the
      // sheet open so the user can retry or request a new code.
    }
  };

  const closeDeleteModal = (): void => {
    setShowDeleteConfirm(false);
    setRequestSent(false);
    setCodeStep(false);
    setCode('');
    setBlockingClubs(null);
  };

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Account & Privacy" onBack={() => navigate('/settings')} />

      <div className="flex-1 overflow-y-auto pt-14">
        <div className="max-w-2xl mx-auto p-4 space-y-8">
          {/* Export my data — GDPR Article 15 */}
          <section>
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 px-1">
              Your Data
            </h2>
            <Card variant="default">
              <div className="space-y-3 p-4">
                <div className="flex items-start gap-3">
                  <div className="text-accent mt-1">
                    <Download className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-text-primary font-medium">Export my data</h3>
                    <p className="text-text-secondary text-sm mt-1">
                      Download a JSON file of your data — profile, club memberships, events, RSVPs,
                      check-ins, messages, invites, and notification preferences.
                    </p>
                  </div>
                </div>
                <div className="pt-1">
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={handleExport}
                    disabled={exportMutation.isPending}
                  >
                    {exportMutation.isPending ? 'Preparing download…' : 'Download my data'}
                  </Button>
                </div>
              </div>
            </Card>
          </section>

          {/* Danger Zone — Delete account, GDPR Article 17 */}
          <section>
            <h2 className="text-xs font-semibold text-oro-error uppercase tracking-wider mb-3 px-1">
              Danger Zone
            </h2>
            <Card variant="default" className="border-oro-error/40">
              <div className="space-y-3 p-4">
                <div className="flex items-start gap-3">
                  <div className="text-oro-error mt-1">
                    <Trash2 className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-text-primary font-medium">Delete my account</h3>
                    <p className="text-text-secondary text-sm mt-1">
                      Permanently delete your account. This cannot be undone.
                    </p>
                    <ul className="text-text-secondary text-sm mt-3 space-y-1 list-disc list-inside">
                      <li>Your profile, RSVPs, check-ins, and personal data will be removed.</li>
                      <li>
                        Your messages will remain in club chat history but will be attributed to{' '}
                        <span className="font-mono text-xs">[Deleted User]</span>.
                      </li>
                      <li>Events you created will transfer to the club owner.</li>
                      <li>You will be signed out on every device.</li>
                      <li>
                        We keep a record that the deletion happened (account ID, timestamp, IP address and
                        browser) for two years, to defend against false deletion claims.
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="pt-1">
                  <Button
                    variant="destructive"
                    size="md"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete my account
                  </Button>
                </div>
              </div>
            </Card>
          </section>
        </div>
      </div>

      {/* Confirm modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-modal-title"
          onClick={() =>
            !requestDeletionMutation.isPending &&
            !confirmByCodeMutation.isPending &&
            !requestSent &&
            !codeStep &&
            closeDeleteModal()
          }
        >
          <div
            className="w-full max-w-md bg-oro-surface border border-oro-border rounded-xl p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            {!requestSent && !codeStep && !blockingClubs && (
              <>
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-oro-error mt-0.5" />
                  <h2 id="delete-modal-title" className="text-text-primary font-semibold text-lg">
                    Confirm account deletion
                  </h2>
                </div>
                <p className="text-text-secondary text-sm">
                  We will send a confirmation to finish deleting your account: an email link, or a
                  text code if you signed up with just a phone. Complete it within 15 minutes to
                  permanently delete your account.
                </p>
                <p className="text-text-secondary text-sm">
                  This is your last chance to back out before it is sent.
                </p>
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="ghost"
                    size="md"
                    onClick={closeDeleteModal}
                    disabled={requestDeletionMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    size="md"
                    onClick={handleRequestDelete}
                    disabled={requestDeletionMutation.isPending}
                  >
                    {requestDeletionMutation.isPending ? 'Sending…' : 'Send confirmation'}
                  </Button>
                </div>
              </>
            )}

            {codeStep && (
              <>
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-oro-error mt-0.5" />
                  <h2 id="delete-modal-title" className="text-text-primary font-semibold text-lg">
                    Enter your deletion code
                  </h2>
                </div>
                <p className="text-text-secondary text-sm">
                  We texted a 6-digit code to your phone. Enter it to permanently delete your
                  account. This cannot be undone.
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  aria-label="Deletion code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  disabled={confirmByCodeMutation.isPending}
                  className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary text-center text-2xl tracking-[0.5em] focus:outline-none focus:border-accent"
                />
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="ghost"
                    size="md"
                    onClick={closeDeleteModal}
                    disabled={confirmByCodeMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    size="md"
                    onClick={handleSubmitCode}
                    disabled={code.length !== 6 || confirmByCodeMutation.isPending}
                  >
                    {confirmByCodeMutation.isPending ? 'Deleting…' : 'Confirm and delete'}
                  </Button>
                </div>
              </>
            )}

            {requestSent && (
              <>
                <h2 id="delete-modal-title" className="text-text-primary font-semibold text-lg">
                  Check your email
                </h2>
                <p className="text-text-secondary text-sm">
                  We sent a confirmation link to your email. Click it within 15 minutes to permanently
                  delete your account.
                </p>
                <p className="text-text-secondary text-sm">
                  If you change your mind, just ignore the email — your account stays as-is.
                </p>
                <div className="pt-2">
                  <Button variant="primary" size="md" onClick={closeDeleteModal}>
                    Got it
                  </Button>
                </div>
              </>
            )}

            {blockingClubs && (
              <>
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-oro-warning mt-0.5" />
                  <h2 id="delete-modal-title" className="text-text-primary font-semibold text-lg">
                    Transfer ownership first
                  </h2>
                </div>
                <p className="text-text-secondary text-sm">
                  You are the sole owner of {blockingClubs.length === 1 ? 'a club' : `${blockingClubs.length} clubs`}.
                  Transfer ownership or delete each club before deleting your account.
                </p>
                <ul className="space-y-2">
                  {blockingClubs.map((club) => (
                    <li key={club.id}>
                      <button
                        onClick={() => navigate(`/clubs/${club.slug}/settings/danger`)}
                        className="w-full flex items-center justify-between gap-3 p-3 bg-oro-surface-alt border border-oro-border rounded-lg hover:border-oro-border-hover transition-colors text-left"
                      >
                        <span className="text-text-primary font-medium">{club.name}</span>
                        <ExternalLink className="w-4 h-4 text-text-muted" />
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="pt-2">
                  <Button variant="ghost" size="md" onClick={closeDeleteModal}>
                    Close
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
